// web/src/api/push.js — client-side Web Push wiring for the customer PWA.
// Registers the service worker, negotiates the browser Push subscription against
// the server's VAPID key, and syncs it to POST/DELETE /api/push/subscribe.

import { apiFetch } from './auth';

// base64url (VAPID public key) → Uint8Array for applicationServerKey.
function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

// True when the browser has everything push needs (Safari < 16.4 / http → false).
export function pushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// 'default' | 'granted' | 'denied' | 'unsupported'
export function pushPermission() {
  return pushSupported() ? Notification.permission : 'unsupported';
}

// Register the service worker (idempotent). Returns the registration or null.
export async function registerServiceWorker() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch {
    return null;
  }
}

// The server's VAPID public key + whether push is configured server-side.
async function getVapidConfig() {
  try {
    return await apiFetch('/push/vapid-public-key');
  } catch {
    return { key: null, enabled: false };
  }
}

// Ask permission (if needed), subscribe via the SW, and POST the subscription to
// the API. Returns true on success. Idempotent — safe to call repeatedly.
export async function enablePush() {
  if (!pushSupported()) return false;
  const cfg = await getVapidConfig();
  if (!cfg || !cfg.enabled || !cfg.key) return false; // push not configured on the server

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return false;

  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  if (!reg) return false;
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(cfg.key),
    });
  }
  await apiFetch('/push/subscribe', {
    method: 'POST',
    body: JSON.stringify({ subscription: sub }),
  });
  return true;
}

// Unsubscribe this device and drop the server row.
export async function disablePush() {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = reg && (await reg.pushManager.getSubscription());
  if (sub) {
    try {
      await apiFetch('/push/subscribe', {
        method: 'DELETE',
        body: JSON.stringify({ endpoint: sub.endpoint }),
      });
    } catch { /* server may already be gone; still unsubscribe locally */ }
    try { await sub.unsubscribe(); } catch { /* best effort */ }
  }
}

// Keep the server row fresh on app open when permission is already granted (the
// browser can silently rotate an endpoint). No prompt — only re-syncs an
// existing grant. Best-effort; never throws.
export async function refreshPushSubscription() {
  if (!pushSupported() || Notification.permission !== 'granted') return;
  try { await enablePush(); } catch { /* ignore */ }
}
